import { useState } from "react";
import {
  InfiniteListBase,
  RecordRepresentation,
  ShowBase,
  useShowContext,
  useTranslate,
} from "ra-core";
import type { ShowBaseProps } from "ra-core";
import { useIsMobile } from "@/hooks/use-mobile";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Pencil } from "lucide-react";
import { Link, useSearchParams } from "react-router";

import MobileHeader from "../layout/MobileHeader";
import { MobileContent } from "../layout/MobileContent";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { NoteCreate, NotesIterator, NotesIteratorMobile } from "../notes";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TagsListEdit } from "./TagsListEdit";
import { ContactEditSheet } from "./ContactEditSheet";
import { ContactClassificationInfo } from "./ContactClassificationInfo";
import { ContactNameReview } from "./ContactNameReview";
import {
  ContactDeveloperSlot,
  ContactOwnerPropertiesSlot,
} from "./contactTypes";
import { ContactPersonalInfo } from "./ContactPersonalInfo";
import { ContactBackgroundInfo } from "./ContactBackgroundInfo";
import { ContactTasksList } from "./ContactTasksList";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import { ContactAside } from "./ContactAside";
import { MobileBackButton } from "../misc/MobileBackButton";
import { ContactExpedienteTab } from "./expediente/ContactExpedienteTab";

/** Route contract with the operation guard: `/contacts/<id>/show?tab=expediente`. */
const EXPEDIENTE_TAB = "expediente";

/** Top-level tab of the contact file, kept in the `?tab=` search param (never in storage). */
const useContactFileTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab =
    searchParams.get("tab") === EXPEDIENTE_TAB ? EXPEDIENTE_TAB : "general";
  const setTab = (next: string) =>
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next === EXPEDIENTE_TAB) params.set("tab", EXPEDIENTE_TAB);
        else params.delete("tab");
        return params;
      },
      { replace: true },
    );
  return { tab, setTab };
};

const ContactFileTabsList = () => {
  const translate = useTranslate();
  return (
    <TabsList className="grid w-full max-w-md grid-cols-2">
      <TabsTrigger value="general">
        {translate("crm.contact_expediente.tab_general")}
      </TabsTrigger>
      <TabsTrigger value={EXPEDIENTE_TAB}>
        {translate("crm.contact_expediente.tab_file")}
      </TabsTrigger>
    </TabsList>
  );
};

export const ContactShow = (props: ShowBaseProps = {}) => {
  const isMobile = useIsMobile();

  return (
    <ShowBase
      queryOptions={{
        onError: isMobile
          ? () => {
              {
                /** Disable error notification as the content handles offline */
              }
            }
          : undefined,
      }}
      {...props}
    >
      {isMobile ? <ContactShowContentMobile /> : <ContactShowContent />}
    </ShowBase>
  );
};

const ContactShowContentMobile = () => {
  const translate = useTranslate();
  const { defaultTitle, record, isPending } = useShowContext<Contact>();
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { tab: fileTab, setTab: setFileTab } = useContactFileTab();
  if (isPending || !record) return null;

  const taskCount = record.nb_tasks ?? 0;

  return (
    <>
      {/* We need to repeat the note creation sheet here to support the note 
      create button that is rendered when there are no notes. */}
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={record.id}
      />
      <ContactEditSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        contactId={record.id}
      />
      <MobileHeader>
        <MobileBackButton />
        <div className="flex flex-1 min-w-0">
          <Link to="/contacts" className="flex-1 min-w-0">
            <h1 className="truncate text-xl font-semibold">{defaultTitle}</h1>
          </Link>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label={translate("ra.action.edit")}
          onClick={() => setEditOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </MobileHeader>
      <MobileContent>
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <Avatar />
            <div className="mx-3 flex-1">
              <h2 className="text-2xl font-bold">
                <RecordRepresentation />
              </h2>
              <ContactNameReview />
              <div className="text-sm text-muted-foreground">
                {record.title && record.company_id != null
                  ? `${translate("resources.contacts.position_at", {
                      title: record.title,
                    })} `
                  : record.title}
                {record.company_id != null && (
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link="show"
                  >
                    <TextField source="name" className="underline" />
                  </ReferenceField>
                )}
              </div>
            </div>
            <div>
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
                className="no-underline"
              >
                <CompanyAvatar />
              </ReferenceField>
            </div>
          </div>
        </div>

        <Tabs
          value={fileTab}
          onValueChange={setFileTab}
          className="w-full gap-4"
        >
          <ContactFileTabsList />
          <TabsContent value={EXPEDIENTE_TAB}>
            <ContactExpedienteTab contact={record} />
          </TabsContent>
          <TabsContent value="general">
            <Tabs defaultValue="notes" className="w-full">
              <TabsList className="grid w-full grid-cols-3 h-10">
                <TabsTrigger value="notes">
                  {translate("resources.notes.name", { smart_count: 2 })}
                </TabsTrigger>
                <TabsTrigger value="tasks">
                  {translate("crm.common.task_count", {
                    smart_count: taskCount ?? 0,
                  })}
                </TabsTrigger>
                <TabsTrigger value="details">
                  {translate("crm.common.details")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="notes" className="mt-2">
                <InfiniteListBase
                  resource="contact_notes"
                  filter={{ contact_id: record.id }}
                  sort={{ field: "date", order: "DESC" }}
                  perPage={25}
                  disableSyncWithLocation
                  storeKey={false}
                  empty={
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                      <p className="text-muted-foreground mb-4">
                        {translate("resources.notes.empty")}
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setNoteCreateOpen(true)}
                      >
                        {translate("resources.notes.action.add")}
                      </Button>
                    </div>
                  }
                  loading={false}
                  error={false}
                  queryOptions={{
                    onError: () => {
                      /** override to hide notification as error case is handled by NotesIteratorMobile */
                    },
                  }}
                >
                  <NotesIteratorMobile contactId={record.id} showStatus />
                </InfiniteListBase>
              </TabsContent>

              <TabsContent value="tasks" className="mt-4">
                <ContactTasksList />
              </TabsContent>

              <TabsContent value="details" className="mt-4">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold">
                      {translate(
                        "resources.contacts.field_categories.classification",
                      )}
                    </h3>
                    <Separator />
                    <div className="mt-3">
                      <ContactClassificationInfo />
                      <ContactOwnerPropertiesSlot contact={record} />
                      <ContactDeveloperSlot contact={record} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {translate(
                        "resources.contacts.field_categories.personal_info",
                      )}
                    </h3>
                    <Separator />
                    <div className="mt-3">
                      <ContactPersonalInfo />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {translate(
                        "resources.contacts.field_categories.background_info",
                      )}
                    </h3>
                    <Separator />
                    <div className="mt-3">
                      <ContactBackgroundInfo />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">
                      {translate("resources.tags.name", { smart_count: 2 })}
                    </h3>
                    <Separator />
                    <div className="mt-3">
                      <TagsListEdit />
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </MobileContent>
    </>
  );
};

const ContactShowContent = () => {
  const translate = useTranslate();
  const { record, isPending } = useShowContext<Contact>();
  const { tab: fileTab, setTab: setFileTab } = useContactFileTab();
  if (isPending || !record) return null;

  return (
    <Tabs value={fileTab} onValueChange={setFileTab} className="mt-2 mb-2">
      <ContactFileTabsList />
      <TabsContent value={EXPEDIENTE_TAB}>
        <Card>
          <CardContent>
            <ContactExpedienteTab contact={record} />
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="general">
        <div className="flex gap-8">
          <div className="flex-1">
            <Card>
              <CardContent>
                <div className="flex">
                  <Avatar />
                  <div className="ml-2 flex-1">
                    <h5 className="text-xl font-semibold">
                      <RecordRepresentation />
                    </h5>
                    <ContactNameReview />
                    <div className="inline-flex text-sm text-muted-foreground">
                      {record.title && record.company_id != null
                        ? `${translate("resources.contacts.position_at", {
                            title: record.title,
                          })} `
                        : record.title}
                      {record.company_id != null && (
                        <ReferenceField
                          source="company_id"
                          reference="companies"
                          link="show"
                        >
                          &nbsp;
                          <TextField source="name" />
                        </ReferenceField>
                      )}
                    </div>
                  </div>
                  <div>
                    <ReferenceField
                      source="company_id"
                      reference="companies"
                      link="show"
                      className="no-underline"
                    >
                      <CompanyAvatar />
                    </ReferenceField>
                  </div>
                </div>
                <InfiniteListBase
                  resource="contact_notes"
                  filter={{ contact_id: record.id }}
                  sort={{ field: "date", order: "DESC" }}
                  perPage={25}
                  disableSyncWithLocation
                  storeKey={false}
                  empty={
                    <NoteCreate
                      reference="contacts"
                      showStatus
                      className="mt-4"
                    />
                  }
                >
                  <NotesIterator reference="contacts" showStatus />
                </InfiniteListBase>
              </CardContent>
            </Card>
          </div>
          <ContactAside />
        </div>
      </TabsContent>
    </Tabs>
  );
};
